import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import { fetchAllPages } from '../../utils/fetchAllPages';
import { LINE_EDITABLE_STATUSES, PO_STATUS_CLASSES, poStatusLabel, selectableStatuses } from './poStatus';
import './PurchaseOrders.css';
import { localToday } from '../../utils/dateUtils';

const lineSum = (lines) => lines.reduce((sum, line) => sum + (parseFloat(line.line_total) || 0), 0);
const nextLineNumber = (lines) => lines.reduce((max, line) => Math.max(max, line.line_number || 0), 0) + 1;

const ViewPurchaseOrder = forwardRef(({ purchaseOrderId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit }, ref) => {
  const { t } = useTranslation('procurement');
  const { t: tCommon } = useTranslation('common');
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [lines, setLines] = useState([]);
  const [showAddLine, setShowAddLine] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [currentPoId, setCurrentPoId] = useState(purchaseOrderId);

  const { showSuccess, showError, showWarning } = useMessages();
  const [lineFormData, setLineFormData] = useState({});

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    setCurrentPoId(purchaseOrderId); // Update current PO ID when prop changes
    fetchSuppliers();
    fetchMaterials();
    if (purchaseOrderId && !createMode) {
      fetchPurchaseOrder();
    } else if (createMode) {
      const newPurchaseOrder = {
        po_number: `PO-${Math.random().toString(36).substr(2, 8).toUpperCase()}`,
        supplier_id: null,
        order_date: localToday(),
        expected_delivery: localToday(14),
        status: 'DRAFT',
        payment_terms: 'NET_30',
        currency: 'USD',
        subtotal: 0,
        total_amount: 0,
        notes: ''
      };
      setPurchaseOrder(newPurchaseOrder);
      setEditData(newPurchaseOrder);
      setLines([]);
      setCurrentPoId(null); // No PO ID in create mode until saved
    }
  }, [purchaseOrderId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  // Ensure lines are fetched when currentPoId changes in view mode
  useEffect(() => {
    if (currentPoId && !createMode && purchaseOrder) {
      fetchLines(currentPoId);
    }
  }, [currentPoId, createMode, purchaseOrder]);

  // Helper function to format datetime to date for HTML date inputs
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    return date.toISOString().split('T')[0];
  };

  const fetchSuppliers = async () => {
    try {
      // Only active suppliers can be chosen; the PO's own supplier is shown even if inactive
      const response = await apiFetch(getApiUrl('/api/providers?is_active=true&page_size=500'));
      if (response.ok) {
        const data = await response.json();
        // Map providers to suppliers format
        const mappedSuppliers = (data.providers || []).map(provider => ({
          supplier_id: provider.provider_id,
          supplier_name: provider.provider_name,
          supplier_code: provider.provider_code,
          supplier_type: provider.provider_type || 'General'
        }));
        setSuppliers(mappedSuppliers);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  // Every active material, not only the first page of 50 (#360)
  const fetchMaterials = async () => {
    try {
      setMaterials(await fetchAllPages('/api/inventory', 'items', { is_active: 'true' }));
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };

  const fetchPurchaseOrder = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/purchase-orders/${purchaseOrderId}`));
      if (response.ok) {
        const data = await response.json();
        setPurchaseOrder(data);
        setEditData(data);
        // Fetch purchase order lines if they exist
        fetchLines(purchaseOrderId);
      } else {
        setError(t('purchaseOrders.view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching purchase order:', error);
      setError(t('purchaseOrders.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchLines = async (targetPoId) => {
    try {
      const response = await apiFetch(getApiUrl(`/api/purchase-orders/${targetPoId}/lines`));
      if (response.ok) {
        const data = await response.json();
        setLines(data || []);
      } else {
        setLines([]);
      }
    } catch (error) {
      console.error('Error fetching lines:', error);
      setLines([]);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);

    // Update supplier name when supplier changes
    if (field === 'supplier_id') {
      const supplier = suppliers.find(s => s.supplier_id === parseInt(value));
      if (supplier) {
        setEditData(prev => ({
          ...prev,
          supplier_name: supplier.supplier_name
        }));
      }
    }
  };

  const handleSave = async () => {
    try {
      setError(null);
      const url = createMode
        ? API_CONFIG.ENDPOINTS.PURCHASE_ORDERS
        : getApiUrl(`/api/purchase-orders/${purchaseOrderId}`);

      const method = createMode ? 'POST' : 'PUT';

      // The number is fixed once created, lines are saved one by one and the total is their sum (#364)
      const payload = {
        supplier_id: editData.supplier_id ? parseInt(editData.supplier_id, 10) : null,
        order_date: formatDateForInput(editData.order_date) || null,
        expected_delivery: formatDateForInput(editData.expected_delivery) || null,
        payment_terms: editData.payment_terms,
        currency: editData.currency,
        notes: editData.notes,
      };
      if (createMode) {
        payload.po_number = editData.po_number;
      } else {
        payload.status = editData.status;
      }

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        setHasChanges(false);

        if (createMode && result.po_id) {
          setPurchaseOrder(result);
          setEditData(result);
          setCurrentPoId(result.po_id); // Update the current PO ID so lines can be added
        } else if (!createMode) {
          await fetchPurchaseOrder();
        }

        showSuccess(
          createMode ? t('purchaseOrders.view.messages.createdTitle') : t('purchaseOrders.view.messages.updatedTitle'),
          createMode ? t('purchaseOrders.view.messages.createdSuccess') : t('purchaseOrders.view.messages.updatedSuccess')
        );

        if (onSave) onSave(result);
      } else {
        await showApiError(response, showError, tCommon, createMode ? t('purchaseOrders.view.messages.createFailed') : t('purchaseOrders.view.messages.updateFailed'));
        return;
      }
    } catch (error) {
      console.error('Error saving purchase order:', error);
      setError(t('purchaseOrders.view.messages.createFailed'));
      showError(
        createMode ? t('purchaseOrders.view.messages.createFailed') : t('purchaseOrders.view.messages.updateFailed'),
        t('purchaseOrders.view.messages.createFailed')
      );
    }
  };

  const handleCancel = () => {
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(purchaseOrder);
      setHasChanges(false);
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('purchaseOrders.view.messages.deleteConfirm'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/purchase-orders/${purchaseOrderId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('purchaseOrders.view.messages.deletedTitle'), t('purchaseOrders.view.messages.deletedSuccess'));
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon, t('purchaseOrders.view.messages.deleteFailed'));
      }
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      setError(t('purchaseOrders.view.messages.deleteFailed'));
      showError(t('purchaseOrders.view.messages.deleteFailed'), t('purchaseOrders.view.messages.deleteFailed'));
    }
  };

  const handleSaveLine = async () => {
    try {
      if (!lineFormData.inventory_item_id || !lineFormData.quantity_ordered || !lineFormData.unit_price) {
        alert(t('purchaseOrders.view.messages.fillRequired'));
        return;
      }

      // Check if we're in create mode without a saved PO
      if (createMode && !currentPoId) {
        alert(t('purchaseOrders.view.lineItems.saveFirstAlert'));
        return;
      }

      const url = editingLine
        ? getApiUrl(`/api/purchase-orders/${currentPoId}/lines/${editingLine.item_id}`)
        : getApiUrl(`/api/purchase-orders/${currentPoId}/lines`);

      const method = editingLine ? 'PUT' : 'POST';

      const lineNumber = parseInt(lineFormData.line_number, 10) || null;
      if (lineNumber && lines.some(l => l.line_number === lineNumber && l.item_id !== editingLine?.item_id)) {
        showError(t('purchaseOrders.view.messages.lineNumberTitle'), t('purchaseOrders.view.messages.lineNumberTaken', { number: lineNumber }));
        return;
      }

      // The received quantity is only written by goods receipts (#365)
      const lineData = {
        inventory_item_id: parseInt(lineFormData.inventory_item_id, 10),
        line_number: lineNumber,
        quantity_ordered: parseFloat(lineFormData.quantity_ordered),
        unit_price: parseFloat(lineFormData.unit_price),
        notes: lineFormData.notes || null
      };

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lineData)
      });

      if (response.ok) {
        showSuccess(
          editingLine ? t('purchaseOrders.view.messages.lineUpdatedTitle') : t('purchaseOrders.view.messages.lineAddedTitle'),
          editingLine ? t('purchaseOrders.view.messages.lineUpdatedSuccess') : t('purchaseOrders.view.messages.lineAddedSuccess')
        );
        await fetchLines(currentPoId);
        setShowAddLine(false);
        setEditingLine(null);
        setLineFormData({});
      } else {
        await showApiError(response, showError, tCommon, editingLine ? t('purchaseOrders.view.messages.lineUpdateFailed') : t('purchaseOrders.view.messages.lineAddFailed'));
      }
    } catch (error) {
      console.error('Error saving line:', error);
      showError(
        editingLine ? t('purchaseOrders.view.messages.lineUpdateFailed') : t('purchaseOrders.view.messages.lineAddFailed'),
        t('purchaseOrders.view.messages.lineAddFailed')
      );
    }
  };

  const handleEditLine = (line) => {
    setEditingLine(line);
    setLineFormData({
      inventory_item_id: line.inventory_item_id,
      line_number: line.line_number,
      quantity_ordered: line.quantity_ordered,
      unit_price: line.unit_price,
      notes: line.notes || ''
    });
    setShowAddLine(true);
  };

  const handleDeleteLine = async (itemId) => {
    if (!window.confirm(t('purchaseOrders.view.messages.lineDeleteConfirm'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/purchase-orders/${currentPoId}/lines/${itemId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('purchaseOrders.view.messages.lineDeletedTitle'), t('purchaseOrders.view.messages.lineDeletedSuccess'));
        await fetchLines(currentPoId);
      } else {
        await showApiError(response, showError, tCommon, t('purchaseOrders.view.messages.lineDeleteFailed'));
      }
    } catch (error) {
      console.error('Error deleting line:', error);
      showError(t('purchaseOrders.view.messages.lineDeleteFailed'), t('purchaseOrders.view.messages.lineDeleteFailed'));
    }
  };

  // After the first receipt the supplier and the lines are fixed (#365). A cancelled receipt gives its
  // quantity back, so received quantities > 0 mean a receipt that still stands.
  const hasReceipts = lines.some(line => parseFloat(line.quantity_received) > 0);
  const linesLocked = !createMode && (hasReceipts || !LINE_EDITABLE_STATUSES.includes(purchaseOrder?.status || 'DRAFT'));

  const renderGeneralTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('purchaseOrders.view.sections.poInfo')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('purchaseOrders.view.fields.poNumber')} *</label>
            <input
              type="text"
              value={editData.po_number || ''}
              onChange={(e) => handleFieldChange('po_number', e.target.value)}
              disabled={!createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('purchaseOrders.view.fields.supplier')} *</label>
            <select
              value={editData.supplier_id || ''}
              onChange={(e) => handleFieldChange('supplier_id', e.target.value)}
              disabled={(!editMode && !createMode) || hasReceipts}
              title={hasReceipts ? t('purchaseOrders.view.hints.supplierLocked') : undefined}
              required
            >
              <option value="" disabled hidden>{t('purchaseOrders.view.placeholders.selectSupplier')}</option>
              {editData.supplier_id && !suppliers.some(s => String(s.supplier_id) === String(editData.supplier_id)) && (
                <option value={editData.supplier_id}>{editData.supplier_name || editData.supplier_id}</option>
              )}
              {suppliers.map(supplier => (
                <option key={supplier.supplier_id} value={supplier.supplier_id}>
                  {supplier.supplier_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('purchaseOrders.view.fields.orderDate')} *</label>
            <input
              type="date"
              value={formatDateForInput(editData.order_date)}
              onChange={(e) => handleFieldChange('order_date', e.target.value)}
              disabled={!editMode && !createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('purchaseOrders.view.fields.expectedDelivery')} *</label>
            <input
              type="date"
              value={formatDateForInput(editData.expected_delivery)}
              onChange={(e) => handleFieldChange('expected_delivery', e.target.value)}
              disabled={!editMode && !createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('purchaseOrders.view.fields.status')}</label>
            <select
              value={editData.status || 'DRAFT'}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              disabled={!editMode}
            >
              {selectableStatuses(purchaseOrder?.status || 'DRAFT', hasReceipts).map(status => (
                <option key={status} value={status}>{poStatusLabel(t, status)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('purchaseOrders.view.fields.currency')}</label>
            <select
              value={editData.currency || 'USD'}
              onChange={(e) => handleFieldChange('currency', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="USD">{t('purchaseOrders.view.currencies.usd')}</option>
              <option value="EUR">{t('purchaseOrders.view.currencies.eur')}</option>
              <option value="DKK">{t('purchaseOrders.view.currencies.dkk')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('purchaseOrders.view.sections.termsConditions')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('purchaseOrders.view.fields.paymentTerms')}</label>
            <select
              value={editData.payment_terms || 'NET_30'}
              onChange={(e) => handleFieldChange('payment_terms', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="UPFRONT">{t('purchaseOrders.view.paymentTerms.upfront')}</option>
              <option value="NET_30">{t('purchaseOrders.view.paymentTerms.net30')}</option>
              <option value="NET_45">{t('purchaseOrders.view.paymentTerms.net45')}</option>
              <option value="NET_60">{t('purchaseOrders.view.paymentTerms.net60')}</option>
              <option value="NET_90">{t('purchaseOrders.view.paymentTerms.net90')}</option>
              <option value="NET_120">{t('purchaseOrders.view.paymentTerms.net120')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('purchaseOrders.view.sections.totals')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('purchaseOrders.view.fields.totalAmount')}</label>
            <input
              type="number"
              step="0.01"
              value={(currentPoId ? lineSum(lines) : 0).toFixed(2)}
              disabled
            />
            <small className="field-hint">{t('purchaseOrders.view.hints.totalFromLines')}</small>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotesTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('purchaseOrders.view.sections.notes')}</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('purchaseOrders.view.fields.internalNotes')}</label>
            <textarea
              value={editData.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('purchaseOrders.view.lineItems.placeholders.notes')}
              rows="6"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderLinesTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <div className="section-header">
          <h3>{t('purchaseOrders.view.sections.lineItems')}</h3>
          {(editMode || createMode) && !linesLocked && (
            <>
              {createMode && !currentPoId ? (
                <div className="btn-disabled btn-sm" title={t('purchaseOrders.view.lineItems.saveFirstTitle')}>
                  {t('purchaseOrders.view.lineItems.addLineButton')} ({t('purchaseOrders.view.lineItems.saveFirstShort')})
                </div>
              ) : (
                <button
                  className="btn-primary btn-sm"
                  onClick={() => {
                    setShowAddLine(true);
                    setEditingLine(null);
                    setLineFormData({
                      inventory_item_id: '',
                      line_number: nextLineNumber(lines),
                      quantity_ordered: 1,
                      unit_price: 0,
                      notes: ''
                    });
                  }}
                >
                  {t('purchaseOrders.view.lineItems.addLineButton')}
                </button>
              )}
            </>
          )}
        </div>

        {linesLocked && (editMode || createMode) && (
          <div className="field-hint">{t('purchaseOrders.view.hints.linesLocked')}</div>
        )}

        {showAddLine && !linesLocked && (
          <div className="contact-form">
            <h4>{editingLine ? t('purchaseOrders.view.lineItems.editLineTitle') : t('purchaseOrders.view.lineItems.addLineTitle')}</h4>
            <div className="form-grid">
              <div className="form-group">
                <label>{t('purchaseOrders.view.lineItems.fields.material')} *</label>
                <select
                  value={lineFormData.inventory_item_id || ''}
                  onChange={(e) => setLineFormData(prev => ({...prev, inventory_item_id: parseInt(e.target.value)}))}
                  required
                >
                  <option value="" disabled hidden>{t('purchaseOrders.view.lineItems.placeholders.selectMaterial')}</option>
                  {editingLine && !materials.some(m => m.item_id === editingLine.inventory_item_id) && (
                    <option value={editingLine.inventory_item_id}>
                      {[editingLine.item_code, editingLine.item_name].filter(Boolean).join(' - ')}
                    </option>
                  )}
                  {materials.map(material => (
                    <option key={material.item_id} value={material.item_id}>
                      {material.item_code ? `${material.item_code} - ${material.item_name}` : material.item_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('purchaseOrders.view.lineItems.fields.lineNumber')}</label>
                <input
                  type="number"
                  value={lineFormData.line_number || ''}
                  onChange={(e) => setLineFormData(prev => ({...prev, line_number: parseInt(e.target.value)}))}
                />
              </div>
              <div className="form-group">
                <label>{t('purchaseOrders.view.lineItems.fields.qtyOrdered')} *</label>
                <input
                  type="number"
                  step="0.001"
                  value={lineFormData.quantity_ordered || ''}
                  onChange={(e) => setLineFormData(prev => ({...prev, quantity_ordered: parseFloat(e.target.value)}))}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('purchaseOrders.view.lineItems.fields.unitPrice')} *</label>
                <input
                  type="number"
                  step="0.0001"
                  value={lineFormData.unit_price || ''}
                  onChange={(e) => setLineFormData(prev => ({...prev, unit_price: parseFloat(e.target.value)}))}
                  required
                />
              </div>
            </div>
            <div className="form-group full-width">
              <label>{t('purchaseOrders.view.lineItems.fields.notes')}</label>
              <textarea
                value={lineFormData.notes || ''}
                onChange={(e) => setLineFormData(prev => ({...prev, notes: e.target.value}))}
                placeholder={t('purchaseOrders.view.lineItems.placeholders.notes')}
                rows="2"
              />
            </div>
            <div className="form-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowAddLine(false);
                  setEditingLine(null);
                  setLineFormData({});
                }}
              >
                {t('purchaseOrders.view.lineItems.buttons.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={() => handleSaveLine()}
              >
                {editingLine ? t('purchaseOrders.view.lineItems.buttons.updateLine') : t('purchaseOrders.view.lineItems.buttons.addLine')}
              </button>
            </div>
          </div>
        )}

        <div className="contacts-table">
          <table>
            <thead>
              <tr>
                <th>{t('purchaseOrders.view.lineItems.tableHeaders.line')}</th>
                <th>{t('purchaseOrders.view.lineItems.tableHeaders.material')}</th>
                <th>{t('purchaseOrders.view.lineItems.tableHeaders.qtyOrdered')}</th>
                <th>{t('purchaseOrders.view.lineItems.tableHeaders.qtyReceived')}</th>
                <th>{t('purchaseOrders.view.lineItems.tableHeaders.unitPrice')}</th>
                <th>{t('purchaseOrders.view.lineItems.tableHeaders.lineTotal')}</th>
                <th>{t('purchaseOrders.view.lineItems.tableHeaders.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">{t('purchaseOrders.view.lineItems.noData')}</td>
                </tr>
              ) : (
                lines.map(line => (
                  <tr key={line.item_id}>
                    <td>{line.line_number}</td>
                    <td>
                      {line.item_name || t('purchaseOrders.defaults.unknown')}
                    </td>
                    <td>{line.quantity_ordered}</td>
                    <td>{line.quantity_received || 0}</td>
                    <td>${parseFloat(line.unit_price).toFixed(4)}</td>
                    <td>${line.line_total ? parseFloat(line.line_total).toFixed(2) : '0.00'}</td>
                    <td>
                      {(editMode || createMode) && !linesLocked && (
                        <>
                          <button
                            className="btn-secondary btn-sm"
                            onClick={() => handleEditLine(line)}
                          >
                            {t('purchaseOrders.view.lineItems.buttons.edit')}
                          </button>
                          <button
                            className="btn-danger btn-sm"
                            onClick={() => handleDeleteLine(line.item_id)}
                          >
                            {t('purchaseOrders.view.lineItems.buttons.delete')}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <div className="view-container"><div className="loading">{t('purchaseOrders.view.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!purchaseOrder && !createMode) {
    return <div className="view-container"><div className="loading">{t('purchaseOrders.view.titles.noPOFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <button className="btn-back" onClick={onBack}>←</button>
          <h1>
            {createMode ? t('purchaseOrders.view.titles.newPO') : editData.po_number || t('purchaseOrders.view.titles.viewPO')}
          </h1>
          {!createMode && purchaseOrder?.status && (
            <span className={`status-badge ${PO_STATUS_CLASSES[purchaseOrder.status] || ''}`}>
              {poStatusLabel(t, purchaseOrder.status)}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              <button className="btn-secondary" onClick={onEdit}>{t('purchaseOrders.toolbar.edit')}</button>
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('purchaseOrders.toolbar.cancel')}</button>
              <button className="btn-primary" onClick={handleSave}>
                {createMode ? t('purchaseOrders.toolbar.create') : t('purchaseOrders.toolbar.save')}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            {t('purchaseOrders.view.tabs.general')}
          </button>
          <button
            className={`tab-item ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            {t('purchaseOrders.view.tabs.notes')}
          </button>
          <button
            className={`tab-item ${activeTab === 'lines' ? 'active' : ''}`}
            onClick={() => setActiveTab('lines')}
          >
            {t('purchaseOrders.view.tabs.lines')}
          </button>
        </div>

        <div className="tab-panels">
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'notes' && renderNotesTab()}
          {activeTab === 'lines' && renderLinesTab()}
        </div>
      </div>
    </div>
  );
});

ViewPurchaseOrder.displayName = 'ViewPurchaseOrder';

export default ViewPurchaseOrder;